import React, { useRef, useState, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { capitalize } from '../../utils/capitise'
import GeoProvider, { useGeoFetch } from '../../components/GeoProvider'
import Map from '../../components/Map'
import toolTipFormatter from '../FluDashboard/toolTipFormatter'
import { endDate, startDate } from '../Dashboard/dates'
import dataDisplayOptions from '../FluDashboard/dataDisplayOptions'
import dataDisplayOptionsCovid from '../CovidDashboard/dataDisplayOptions'
import MapLegend from '../FluDashboard/cdcMapLegend8LevelHorizontal'
import MapLegendCovid from '../CovidDashboard/cdcMapLegend8LevelHorizontal'
import styles from './MSAHeatMapNew.module.scss'
import { displayDateReport } from '../FluDashboard/dates'
import { useStateDataFetch } from '../../hooks/useStateDataFetch'
import { useCBSADataFetch } from '../../hooks/useCBSADataFetch'
import { useMSADataFetch } from '../../hooks/useMSADataFetch'
import { useStatesJson } from '../../hooks/useStatesJson'
import EmptyMap from '../../components/Map/emptyMap'
import ErrorMessage from '../../components/Layout/ErrorMessage'
import { captureImage } from './utils/helper'

const MSAHeatMapNew = () => {
  //path ---> /RiskMap/disease/flu/state/TX
  const location = useLocation()
  const locationValues = location?.pathname?.split('/')
  const geoCode = locationValues.at(-1).toUpperCase()
  const diseaseType = locationValues.at(-3)
  const diseaseLabel = capitalize(diseaseType)
  const DEFAULT_MAP_EXPORT = false

  const queryParams = new URLSearchParams(location.search)
  const exportMap = queryParams.get('export')
  const currentIndex = queryParams.get('index')
  const ref = useRef(null)

  let enableMapExport = exportMap === 'true' ? true : DEFAULT_MAP_EXPORT

  const chartRef = useRef(null)
  const [cbsaJSONData, setCbsaJSONData] = useState()
  const [countyJSONData, setCountyJSONData] = useState()

  const {
    data: statesMapData,
    loading: statesLoading,
    error: statesMapDataError,
  } = useStateDataFetch(startDate, endDate, diseaseType)

  const {
    data: cbsaData,
    loading: cbsaLoading,
    error: cbsaError,
  } = useCBSADataFetch(startDate, endDate, diseaseType, geoCode)

  const {
    data: msaMapData,
    loading: msaLoading,
    error: msaMapError,
  } = useMSADataFetch(startDate, endDate, diseaseType)

  const { data: stateJson } = useStatesJson(`${diseaseLabel.toLowerCase()}`)

  const filteredStateJSON = useMemo(() => {
    const filteredState = stateJson?.features?.find(
      (state) => state.properties['hc-a2']?.toUpperCase() === geoCode
    )
    if (filteredState) {
      return { ...stateJson, features: [filteredState] }
    } else {
      return stateJson
    }
  }, [geoCode, stateJson])

  useEffect(() => {
    async function fetchCountyMapData() {
      try {
        let url = `${window.location.origin}/${diseaseLabel.toLowerCase() ==="flu"?'flu':'covid'}/county.json`
        const response = await fetch(url)
        const data = await response.json()
        const filteredCounties = data?.features?.filter((feature) => {
          return feature.properties.STUSPS?.toUpperCase() === geoCode
        })
        const updatedCountyObj = {
          ...data,
          features: [...filteredCounties],
        }
        setCountyJSONData(updatedCountyObj)
      } catch (error) {
        console.log(error, 'Failed to fetch County JSON data ')
      }
    }

    fetchCountyMapData()
  }, [])

  useEffect(() => {
    async function fetchCBSAJsonData() {
      try {
        let url = `${window.location.origin}/${diseaseLabel.toLowerCase() ==="flu"?'flu':'covid'}/cb_2018_us_cbsa_20m_wkid102004.json`
        const response = await fetch(url)
        const data = await response.json()
        // activeCbsas.includes(feat.properties.geoid)
        // if (activeCbsas.includes(feat.properties.geoid)) {
        //   filteredFeats.push(feat)
        //   }
        const filteredCbsaStateWise = data?.features?.filter((feature) =>
          // feature.properties.NAME.split(',')[1].includes(geoCode)
          cbsaData.includes(feature.properties.geoid)
        )

        const updatedCBSAObj = {
          ...data,
          features: [...filteredCbsaStateWise],
        }

        setCbsaJSONData(updatedCBSAObj)
      } catch (error) {
        console.log(error, 'Failed to fetch CBSA JSON data ')
      }
    }
    if (cbsaData?.length > 0) {
      fetchCBSAJsonData()
    }
  }, [cbsaData])

  useEffect(() => {
    if (
      !statesLoading &&
      !msaLoading &&
      !cbsaLoading &&
      msaMapData &&
      statesMapData &&
      ref.current
    ) {
      captureImage(
        `${diseaseType}_STATE_${geoCode}`,
        geoCode,
        +currentIndex,
        diseaseType
      )
    }
  }, [statesLoading, msaLoading, cbsaLoading])

  if (statesMapDataError || cbsaError || msaMapError) {
    return (
      <ErrorMessage
        errorCode={'Error'}
        errorMessage={'Failed to load the map'}
        errorSubject={'Map load failed'}
        errorDetails={
          'Some error occurred. Please refresh your screen and try again.'
        }
      />
    )
  }
  return (
    <div className={styles.base} id="map">
      {(statesLoading || msaLoading || cbsaLoading) && (
        <EmptyMap stateJson={stateJson} />
      )}
      {statesMapData &&
        msaMapData &&
        stateJson &&
        !cbsaLoading &&
        !statesLoading &&
        !msaLoading &&
        countyJSONData && (
          <div className={styles.map_container} ref={ref}>
           { diseaseLabel.toLowerCase() ==="flu" ?(<MapLegend />):(<MapLegendCovid />)}
            <GeoProvider displayDate={displayDateReport}>
              <Map
                toolTipFormatter={toolTipFormatter}
                activeCbsasData={cbsaData ? cbsaData : null}
                statesData={statesMapData}
                cbsaData={msaMapData}
                mapOptions={diseaseLabel.toLowerCase() ==="flu"? (dataDisplayOptions[0]['options'][0]):(dataDisplayOptionsCovid[0]['options'][0])}
                dynamicMapTitle={true}
                mapType={cbsaData ? 'rsvmapmsa' : 'rsvmap'}
                chartRef={chartRef}
                mapDataType={1}
                JsonData={cbsaJSONData ? cbsaJSONData : null}
                stateJson={filteredStateJSON}
                dashboardType="flu"
                zip3Data={null}
                countyData={null}
                countyGeoJson={countyJSONData}
                mapHeight={600}
                mapWidth={1200}
                highlightCBSA={true}
                exportMap={enableMapExport}
              />
            </GeoProvider>
          </div>
        )}
    </div>
  )
}

export default MSAHeatMapNew
